/**
 * Special Cards Screen
 * Every special card, Help above and Hinder below, drawn as in the game
 * (name above the card) and all on one screen. Tap a card to see it full
 * size, then tap it again to turn it over to its effect.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SPECIAL_CARD_INFO, SpecialCardSign } from '@pokercubed/shared';
import SpecialCard from '../GameTable/SpecialCard';

const bySign = (sign: SpecialCardSign) =>
  Object.values(SPECIAL_CARD_INFO)
    .filter((info) => info.sign === sign)
    .sort((a, b) => a.name.localeCompare(b.name));

const SECTIONS = [
  { sign: SpecialCardSign.POSITIVE, title: 'Help ↑', tone: 'text-emerald-400' },
  { sign: SpecialCardSign.NEGATIVE, title: 'Hinder ↓', tone: 'text-rose-400' },
] as const;

// 7 a row (4 rows in all); a phone on its side: 14 a row (2 rows). Cards are
// as wide as the column allows, but never so tall the rows don't fit the screen.
const GRID =
  'grid grid-cols-7 short:grid-cols-[repeat(14,minmax(0,1fr))] [--rows:4] short:[--rows:2] gap-x-1.5 gap-y-4 sm:gap-x-3 justify-items-center pt-3';
const CARD_W = { width: 'min(100%, calc((100dvh - 190px) / var(--rows) / 1.4 - 12px), 110px)' };

const SpecialCardsScreen: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <header className="shrink-0 bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            // Back to wherever you came from (a game or the lobby).
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/lobby'))}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Back
          </button>
          <div className="text-lg font-bold text-white">Special Cards</div>
          <div className="ml-auto text-xs text-gray-500">Tap a card to enlarge it, tap again to flip it</div>
        </div>
      </header>

      <div className="flex-1 min-h-0 w-full max-w-6xl mx-auto px-4 py-2 flex flex-col justify-evenly">
        {SECTIONS.map(({ sign, title, tone }) => (
          <section key={sign} data-testid={`specials-${sign}`}>
            <h2 className={`text-base font-bold ${tone}`}>{title}</h2>
            <div className={GRID}>
              {bySign(sign).map((info) => (
                <div key={info.type} style={CARD_W}>
                  <SpecialCard special={info.type} tapToOpen className="w-full" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default SpecialCardsScreen;
